import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Loader2, Plus, Save, Trash2 } from 'lucide-react';
import { useApiClient } from '../lib/apiClient';
import { useI18n } from '../i18n/LanguageContext';
import type {
  PatientTreatmentPlan,
  PatientTreatmentPlanListResponse,
  TreatmentPlanCareTeamMember,
  TreatmentPlanConfigResponse,
  TreatmentPlanEducation,
  TreatmentPlanEscalationRule,
  TreatmentPlanFollowUp,
  TreatmentPlanGoal,
  TreatmentPlanIntervention,
  TreatmentPlanMedication,
  TreatmentPlanMonitoring,
  TreatmentPlanPriority,
  TreatmentPlanStatus,
} from '../lib/treatmentPlans';
import { formatTreatmentPlanDate } from '../lib/treatmentPlans';
import { SelectMenu } from '../ui/SelectMenu';

type TreatmentPlanAppointmentRow = {
  id: string;
  title: string | null;
  treatmentPlanId: string | null;
  startTime: string;
  endTime: string;
  status: string;
  appointmentType: {
    name: string;
  };
  provider: {
    name: string;
  };
};

type EditablePlan = {
  title: string;
  diagnosisCodes: string[];
  category: string;
  priority: TreatmentPlanPriority;
  status: TreatmentPlanStatus;
  suggestedVisitCount: string;
  startDate: string;
  targetEndDate: string;
  reviewCadenceDays: string;
  nextReviewAt: string;
  progressPercent: string;
  adherencePercent: string;
  patientInstructions: string;
  clinicianNotes: string;
  barriersText: string;
  outcomesText: string;
  goals: TreatmentPlanGoal[];
  interventions: TreatmentPlanIntervention[];
  medications: TreatmentPlanMedication[];
  monitoring: TreatmentPlanMonitoring[];
  followUps: TreatmentPlanFollowUp[];
  education: TreatmentPlanEducation[];
  escalationRules: TreatmentPlanEscalationRule[];
  careTeam: TreatmentPlanCareTeamMember[];
};

const priorities: TreatmentPlanPriority[] = ['LOW', 'NORMAL', 'HIGH', 'URGENT'];
const statuses: TreatmentPlanStatus[] = ['DRAFT', 'ACTIVE', 'ON_HOLD', 'COMPLETED', 'DISCONTINUED'];

function toInputDateTime(value?: string | null) {
  if (!value) return '';
  const date = new Date(value);
  const offset = date.getTimezoneOffset();
  const local = new Date(date.getTime() - offset * 60_000);
  return local.toISOString().slice(0, 16);
}

function toInt(value: string): number | undefined {
  if (!value.trim()) return undefined;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? Math.trunc(numeric) : undefined;
}

function normalizeDiagnosisCodes(values: string[]) {
  const next: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const cleaned = value.trim();
    if (!cleaned || seen.has(cleaned)) continue;
    seen.add(cleaned);
    next.push(cleaned);
    if (next.length >= 3) break;
  }
  return next;
}

function padDiagnosisCodes(values: string[]) {
  const normalized = normalizeDiagnosisCodes(values);
  while (normalized.length < 3) normalized.push('');
  return normalized.slice(0, 3);
}

function toEditablePlan(plan: PatientTreatmentPlan): EditablePlan {
  return {
    title: plan.title || '',
    diagnosisCodes: padDiagnosisCodes(plan.diagnosisCodes || []),
    category: plan.category || '',
    priority: (plan.priority || 'NORMAL') as TreatmentPlanPriority,
    status: plan.status,
    suggestedVisitCount: plan.suggestedVisitCount !== null && plan.suggestedVisitCount !== undefined ? String(plan.suggestedVisitCount) : '',
    startDate: toInputDateTime(plan.startDate),
    targetEndDate: toInputDateTime(plan.targetEndDate),
    reviewCadenceDays: plan.reviewCadenceDays ? String(plan.reviewCadenceDays) : '',
    nextReviewAt: toInputDateTime(plan.nextReviewAt),
    progressPercent: String(plan.progressPercent ?? 0),
    adherencePercent: plan.adherencePercent !== null && plan.adherencePercent !== undefined ? String(plan.adherencePercent) : '',
    patientInstructions: plan.patientInstructions || '',
    clinicianNotes: plan.clinicianNotes || '',
    barriersText: (plan.barriers || []).join(', '),
    outcomesText: (plan.outcomes || []).join(', '),
    goals: Array.isArray(plan.goals) ? (plan.goals as TreatmentPlanGoal[]) : [],
    interventions: Array.isArray(plan.interventions) ? (plan.interventions as TreatmentPlanIntervention[]) : [],
    medications: Array.isArray(plan.medications) ? (plan.medications as TreatmentPlanMedication[]) : [],
    monitoring: Array.isArray(plan.monitoring) ? (plan.monitoring as TreatmentPlanMonitoring[]) : [],
    followUps: Array.isArray(plan.followUps) ? (plan.followUps as TreatmentPlanFollowUp[]) : [],
    education: Array.isArray(plan.education) ? (plan.education as TreatmentPlanEducation[]) : [],
    escalationRules: Array.isArray(plan.escalationRules) ? (plan.escalationRules as TreatmentPlanEscalationRule[]) : [],
    careTeam: Array.isArray(plan.careTeam) ? (plan.careTeam as TreatmentPlanCareTeamMember[]) : [],
  };
}

function statusBadge(status: string) {
  const map: Record<string, string> = {
    DRAFT: 'bg-slate-100 text-slate-700',
    ACTIVE: 'bg-emerald-100 text-emerald-700',
    ON_HOLD: 'bg-amber-100 text-amber-800',
    COMPLETED: 'bg-blue-100 text-blue-700',
    DISCONTINUED: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}

function appointmentStatusBadge(status: string) {
  const map: Record<string, string> = {
    SCHEDULED: 'bg-slate-100 text-slate-700',
    PORTAL_BOOKED: 'bg-cyan-100 text-cyan-700',
    CHECKED_IN: 'bg-amber-100 text-amber-800',
    COMPLETED: 'bg-emerald-100 text-emerald-700',
    CANCELLED: 'bg-rose-100 text-rose-700',
    NO_SHOW: 'bg-rose-100 text-rose-700',
  };
  return map[status] || 'bg-slate-100 text-slate-700';
}

function formatCountLabel(value: number, singular: string, plural: string) {
  return `${value} ${value === 1 ? singular : plural}`;
}

export function PatientTreatmentPlansModule({ patientId }: { patientId: string }) {
  const { fetchJson, fetchApi } = useApiClient();
  const { tr } = useI18n();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [templates, setTemplates] = useState<Array<{ code: string; name: string; active: boolean }>>([]);
  const [plans, setPlans] = useState<PatientTreatmentPlan[]>([]);
  const [appointments, setAppointments] = useState<TreatmentPlanAppointmentRow[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [draft, setDraft] = useState<EditablePlan | null>(null);
  const [createTemplateCode, setCreateTemplateCode] = useState('');
  const [createTitle, setCreateTitle] = useState('');
  const [createPriority, setCreatePriority] = useState<TreatmentPlanPriority>('NORMAL');
  const [createDiagnosisCodes, setCreateDiagnosisCodes] = useState(['', '', '']);
  const [createSuggestedVisitCount, setCreateSuggestedVisitCount] = useState('');
  const [createStartDate, setCreateStartDate] = useState('');
  const [createTargetEndDate, setCreateTargetEndDate] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [banner, setBanner] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedPlan = useMemo(() => plans.find((plan) => plan.id === selectedPlanId) || null, [plans, selectedPlanId]);
  const sortedAppointments = useMemo(
    () =>
      [...appointments]
        .filter((appointment) => appointment.status !== 'CANCELLED' && appointment.status !== 'NO_SHOW')
        .sort((left, right) => new Date(right.startTime).getTime() - new Date(left.startTime).getTime()),
    [appointments],
  );
  const selectedPlanAppointments = useMemo(
    () => sortedAppointments.filter((appointment) => appointment.treatmentPlanId === selectedPlanId),
    [selectedPlanId, sortedAppointments],
  );
  const recentEligibleAppointments = useMemo(
    () => sortedAppointments.filter((appointment) => !appointment.treatmentPlanId).slice(0, 12),
    [sortedAppointments],
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [templateConfig, planData, appointmentData] = await Promise.all([
        fetchJson<TreatmentPlanConfigResponse>('/treatment-plans/templates'),
        fetchJson<PatientTreatmentPlanListResponse>(`/treatment-plans/patients/${patientId}/plans?limit=100`),
        fetchJson<TreatmentPlanAppointmentRow[]>(`/appointments/patient/${patientId}`),
      ]);

      const nextTemplates = (templateConfig.templates || []).map((template) => ({
        code: template.code,
        name: template.name,
        active: Boolean(template.active),
      }));
      setTemplates(nextTemplates);
      if (!createTemplateCode && nextTemplates.length) {
        setCreateTemplateCode(nextTemplates.find((template) => template.active)?.code || nextTemplates[0].code);
      }

      const rows = Array.isArray(planData.data) ? planData.data : [];
      setPlans(rows);
      setAppointments(Array.isArray(appointmentData) ? appointmentData : []);
      if (rows.length) {
        const pick = selectedPlanId ? rows.find((plan) => plan.id === selectedPlanId) || rows[0] : rows[0];
        setSelectedPlanId(pick.id);
        setDraft(toEditablePlan(pick));
      } else {
        setSelectedPlanId(null);
        setDraft(null);
      }
    } catch (err: any) {
      setError(err?.message || tr('Failed to load treatment plans.'));
    } finally {
      setLoading(false);
    }
  }, [createTemplateCode, fetchJson, patientId, selectedPlanId, tr]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!selectedPlan) {
      setDraft(null);
      return;
    }
    setDraft(toEditablePlan(selectedPlan));
  }, [selectedPlan]);

  useEffect(() => {
    if (!createDialogOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCreateDialogOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [createDialogOpen]);

  function openCreateDialog() {
    setError(null);
    setBanner(null);
    if (!createTemplateCode && templates.length) {
      setCreateTemplateCode(templates.find((template) => template.active)?.code || templates[0].code);
    }
    setCreateTitle('');
    setCreatePriority('NORMAL');
    setCreateDiagnosisCodes(['', '', '']);
    setCreateSuggestedVisitCount('');
    setCreateStartDate('');
    setCreateTargetEndDate('');
    setCreateDialogOpen(true);
  }

  async function createPlan() {
    if (!createTitle.trim()) {
      setError(tr('Plan name is required.'));
      return;
    }

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/treatment-plans/patients/${patientId}/plans`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          templateCode: createTemplateCode || undefined,
          title: createTitle.trim(),
          priority: createPriority,
          diagnosisCodes: normalizeDiagnosisCodes(createDiagnosisCodes),
          suggestedVisitCount: toInt(createSuggestedVisitCount),
          startDate: createStartDate ? new Date(createStartDate).toISOString() : undefined,
          targetEndDate: createTargetEndDate ? new Date(createTargetEndDate).toISOString() : undefined,
          status: 'ACTIVE',
        }),
      });
      if (!response.ok) throw new Error(`Create failed (${response.status})`);
      const created = (await response.json()) as PatientTreatmentPlan;
      setPlans((prev) => [created, ...prev]);
      setSelectedPlanId(created.id);
      setDraft(toEditablePlan(created));
      setBanner(tr('Treatment plan assigned.'));
      setCreateTitle('');
      setCreateDiagnosisCodes(['', '', '']);
      setCreateSuggestedVisitCount('');
      setCreateStartDate('');
      setCreateTargetEndDate('');
      setCreateDialogOpen(false);
    } catch (err: any) {
      setError(err?.message || tr('Unable to create treatment plan.'));
    } finally {
      setSaving(false);
    }
  }

  async function savePlan() {
    if (!selectedPlan || !draft) return;

    setSaving(true);
    setError(null);
    setBanner(null);
    try {
      const payload = {
        title: draft.title,
        diagnosisCodes: normalizeDiagnosisCodes(draft.diagnosisCodes),
        category: draft.category || null,
        priority: draft.priority,
        status: draft.status,
        suggestedVisitCount: draft.suggestedVisitCount ? Number(draft.suggestedVisitCount) : null,
        startDate: draft.startDate ? new Date(draft.startDate).toISOString() : null,
        targetEndDate: draft.targetEndDate ? new Date(draft.targetEndDate).toISOString() : null,
        reviewCadenceDays: draft.reviewCadenceDays ? Number(draft.reviewCadenceDays) : null,
        nextReviewAt: draft.nextReviewAt ? new Date(draft.nextReviewAt).toISOString() : null,
        progressPercent: Number(draft.progressPercent || 0),
        adherencePercent: draft.adherencePercent ? Number(draft.adherencePercent) : null,
        patientInstructions: draft.patientInstructions || null,
        clinicianNotes: draft.clinicianNotes || null,
        barriers: draft.barriersText.split(',').map((item) => item.trim()).filter(Boolean),
        outcomes: draft.outcomesText.split(',').map((item) => item.trim()).filter(Boolean),
        goals: draft.goals,
        interventions: draft.interventions,
        medications: draft.medications,
        monitoring: draft.monitoring,
        followUps: draft.followUps,
        education: draft.education,
        escalationRules: draft.escalationRules,
        careTeam: draft.careTeam,
      };

      const response = await fetchApi(`/treatment-plans/patients/${patientId}/plans/${selectedPlan.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error(`Save failed (${response.status})`);
      const updated = (await response.json()) as PatientTreatmentPlan;
      setPlans((prev) => prev.map((plan) => (plan.id === updated.id ? updated : plan)));
      setDraft(toEditablePlan(updated));
      setBanner(tr('Treatment plan saved.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to save treatment plan.'));
    } finally {
      setSaving(false);
    }
  }

  async function setAppointmentTreatmentPlanLink(appointmentId: string, treatmentPlanId: string | null) {
    if (!selectedPlanId) return;
    setBusyAction(`appointment:${appointmentId}`);
    setError(null);
    setBanner(null);
    try {
      const path = `/treatment-plans/patients/${patientId}/plans/${selectedPlanId}/appointments/${appointmentId}`;
      const response = await fetchApi(path, {
        method: treatmentPlanId ? 'POST' : 'DELETE',
      });
      if (!response.ok) throw new Error(`Visit link update failed (${response.status})`);
      await loadData();
      setBanner(treatmentPlanId ? tr('Visit linked to treatment plan.') : tr('Visit removed from treatment plan.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to update linked visits.'));
    } finally {
      setBusyAction(null);
    }
  }

  async function markReviewed() {
    if (!selectedPlan) return;
    setBusyAction('review');
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/treatment-plans/patients/${patientId}/plans/${selectedPlan.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ markReviewed: true }),
      });
      if (!response.ok) throw new Error(`Review failed (${response.status})`);
      const updated = (await response.json()) as PatientTreatmentPlan;
      setPlans((prev) => prev.map((plan) => (plan.id === updated.id ? updated : plan)));
      setDraft(toEditablePlan(updated));
      setBanner(tr('Plan review logged.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to mark review.'));
    } finally {
      setBusyAction(null);
    }
  }

  async function deletePlan(plan: PatientTreatmentPlan) {
    if (!window.confirm(tr('Delete treatment plan?'))) return;
    setBusyAction(plan.id);
    setError(null);
    setBanner(null);
    try {
      const response = await fetchApi(`/treatment-plans/patients/${patientId}/plans/${plan.id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(`Delete failed (${response.status})`);
      const next = plans.filter((item) => item.id !== plan.id);
      setPlans(next);
      if (!next.length) {
        setSelectedPlanId(null);
        setDraft(null);
      } else {
        setSelectedPlanId(next[0].id);
        setDraft(toEditablePlan(next[0]));
      }
      setBanner(tr('Treatment plan deleted.'));
    } catch (err: any) {
      setError(err?.message || tr('Unable to delete treatment plan.'));
    } finally {
      setBusyAction(null);
    }
  }

  function updateGoal(index: number, patch: Partial<TreatmentPlanGoal>) {
    setDraft((prev) => (prev ? { ...prev, goals: prev.goals.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev));
  }

  function updateIntervention(index: number, patch: Partial<TreatmentPlanIntervention>) {
    setDraft((prev) =>
      prev ? { ...prev, interventions: prev.interventions.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  function updateMedication(index: number, patch: Partial<TreatmentPlanMedication>) {
    setDraft((prev) =>
      prev ? { ...prev, medications: prev.medications.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  function updateMonitoring(index: number, patch: Partial<TreatmentPlanMonitoring>) {
    setDraft((prev) =>
      prev ? { ...prev, monitoring: prev.monitoring.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  function updateFollowUp(index: number, patch: Partial<TreatmentPlanFollowUp>) {
    setDraft((prev) =>
      prev ? { ...prev, followUps: prev.followUps.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  function updateEducation(index: number, patch: Partial<TreatmentPlanEducation>) {
    setDraft((prev) =>
      prev ? { ...prev, education: prev.education.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  function updateEscalation(index: number, patch: Partial<TreatmentPlanEscalationRule>) {
    setDraft((prev) =>
      prev ? { ...prev, escalationRules: prev.escalationRules.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  function updateCareTeam(index: number, patch: Partial<TreatmentPlanCareTeamMember>) {
    setDraft((prev) =>
      prev ? { ...prev, careTeam: prev.careTeam.map((item, idx) => (idx === index ? { ...item, ...patch } : item)) } : prev,
    );
  }

  if (loading) {
    return <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">{tr('Loading treatment plans...')}</div>;
  }

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3">
          <div>
            <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Treatment Plans')}</p>
            <h3 className="text-base font-semibold text-slate-900">{tr('Patient Treatment Planning Workspace')}</h3>
          </div>
          <button
            onClick={openCreateDialog}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-lime-400 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-lime-300"
          >
            <Plus size={14} /> {tr('Add treatment plan')}
          </button>
        </div>
        <div className="px-4 py-3 text-sm text-slate-600">
          {tr('Start with a simple concern-based plan or assign a settings-defined template, then manage goals, interventions, medication, monitoring, and outcomes.')}
        </div>
      </div>

      {banner && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">{banner}</div>}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-100 rounded-lg px-3 py-2">{error}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-5 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm text-slate-600">
            {plans.length} {tr('total')}
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-medium">{tr('Plan')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Status')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Priority')}</th>
                  <th className="px-4 py-3 font-medium">{tr('Review')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {plans.map((plan) => (
                  <tr
                    key={plan.id}
                    onClick={() => setSelectedPlanId(plan.id)}
                    className={`cursor-pointer hover:bg-slate-50 ${selectedPlanId === plan.id ? 'bg-slate-50' : ''}`}
                  >
                    <td className="px-4 py-3 align-top">
                      <div className="font-medium text-slate-900">{plan.title}</div>
                      <div className="text-xs text-slate-500">
                        {plan.templateName || plan.templateCode || tr('Custom')}
                        {plan.suggestedVisitCount ? ` · ${plan.suggestedVisitCount} planned visits` : ''}
                      </div>
                    </td>
                    <td className="px-4 py-3 align-top">
                      <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${statusBadge(plan.status)}`}>{tr(plan.status)}</span>
                    </td>
                    <td className="px-4 py-3 align-top text-slate-700">{tr(plan.priority || 'NORMAL')}</td>
                    <td className="px-4 py-3 align-top text-slate-600">{formatTreatmentPlanDate(plan.nextReviewAt)}</td>
                  </tr>
                ))}
                {!plans.length ? (
                  <tr>
                    <td className="px-4 py-6 text-sm text-slate-500" colSpan={4}>
                      {tr('No treatment plans assigned yet.')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>

        <div className="xl:col-span-7 rounded-xl border border-slate-200 bg-white p-4 space-y-4">
          {!selectedPlan || !draft ? (
            <div className="text-sm text-slate-500">{tr('Select a treatment plan to edit.')}</div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h4 className="text-base font-semibold text-slate-900">{selectedPlan.title}</h4>
                <div className="flex items-center gap-2">
                  <button className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm" onClick={markReviewed} disabled={busyAction === 'review'}>
                    {busyAction === 'review' ? <Loader2 size={14} className="animate-spin" /> : null}
                    {tr('Mark Reviewed')}
                  </button>
                  <button className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-sm text-rose-700" onClick={() => deletePlan(selectedPlan)} disabled={busyAction === selectedPlan.id}>
                    {busyAction === selectedPlan.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} {tr('Delete')}
                  </button>
                  <button className="inline-flex items-center gap-2 rounded-lg bg-ink px-3 py-1.5 text-sm text-white" onClick={savePlan} disabled={saving}>
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {tr('Save')}
                  </button>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-slate-50 via-white to-lime-50 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${statusBadge(selectedPlan.status)}`}>{tr(selectedPlan.status)}</span>
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700">{tr(selectedPlan.priority || 'NORMAL')}</span>
                      <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700">{selectedPlan.templateName || selectedPlan.templateCode || tr('Custom plan')}</span>
                    </div>
                    <div>
                      <h5 className="text-lg font-semibold text-slate-900">{selectedPlan.title}</h5>
                      <p className="text-sm text-slate-600">
                        {selectedPlan.diagnosisCodes.length ? selectedPlan.diagnosisCodes.join(', ') : selectedPlan.diagnosis || tr('No diagnosis code added yet.')}
                      </p>
                    </div>
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Review Status')}</div>
                    <div className="mt-1 font-medium text-slate-900">
                      {!selectedPlan.nextReviewAt
                        ? tr('No review scheduled')
                        : new Date(selectedPlan.nextReviewAt).getTime() < Date.now()
                          ? tr('Review overdue')
                          : tr('Review scheduled')}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{formatTreatmentPlanDate(selectedPlan.nextReviewAt)}</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-2 md:grid-cols-3 xl:grid-cols-4">
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Visit Progress')}</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">
                      {selectedPlan.visitProgress.attendedVisitCount}
                      {selectedPlan.suggestedVisitCount ? ` / ${selectedPlan.suggestedVisitCount}` : ''}
                    </div>
                    <div className="mt-1 text-xs text-slate-500">{formatCountLabel(selectedPlan.visitProgress.upcomingVisitCount, tr('upcoming visit'), tr('upcoming visits'))}</div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Clinical Structure')}</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{draft.goals.length + draft.interventions.length + draft.medications.length}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {formatCountLabel(draft.goals.length, tr('goal'), tr('goals'))} · {formatCountLabel(draft.interventions.length, tr('intervention'), tr('interventions'))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Linked Activity')}</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{selectedPlan.visitProgress.linkedAppointmentCount + selectedPlan.linkedNotes.length}</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {formatCountLabel(selectedPlan.visitProgress.linkedAppointmentCount, tr('visit linked'), tr('visits linked'))} · {formatCountLabel(selectedPlan.linkedNotes.length, tr('note linked'), tr('notes linked'))}
                    </div>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-white px-3 py-3">
                    <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Progress')}</div>
                    <div className="mt-1 text-lg font-semibold text-slate-900">{selectedPlan.progressPercent}%</div>
                    <div className="mt-1 text-xs text-slate-500">
                      {selectedPlan.adherencePercent !== null && selectedPlan.adherencePercent !== undefined
                        ? `${tr('Adherence')} ${selectedPlan.adherencePercent}%`
                        : tr('Adherence not recorded')}
                    </div>
                  </div>
                </div>
              </div>

              <details open className="rounded-xl border border-slate-200 bg-white overflow-hidden group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-900">
                  <div>
                    <div>{tr('Care Activity')}</div>
                    <div className="mt-1 text-xs font-normal text-slate-500">{tr('Visits, notes, and near-term plan movement stay visible here.')}</div>
                  </div>
                  <span className="text-xs text-slate-500 group-open:hidden">{tr('Expand')}</span>
                  <span className="text-xs text-slate-500 hidden group-open:inline">{tr('Collapse')}</span>
                </summary>
                <div className="space-y-4 border-t border-slate-100 p-4">
                  <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Last Attended')}</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">{formatTreatmentPlanDate(selectedPlan.visitProgress.lastAttendedVisitAt)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Next Scheduled Visit')}</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">{formatTreatmentPlanDate(selectedPlan.visitProgress.nextScheduledVisitAt)}</div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                      <div className="text-xs uppercase tracking-wide text-slate-500">{tr('Linked Notes')}</div>
                      <div className="mt-1 text-sm font-medium text-slate-900">{selectedPlan.linkedNotes.length}</div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-3 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h5 className="text-sm font-semibold text-slate-900">{tr('Linked Visits')}</h5>
                        <p className="text-xs text-slate-500">{tr('All visits already linked to this treatment plan stay visible here.')}</p>
                      </div>
                      <div className="text-xs text-slate-500">{selectedPlanAppointments.length} {tr('linked')}</div>
                    </div>
                    <div className="space-y-2">
                      {selectedPlanAppointments.map((appointment) => {
                        const actionKey = `appointment:${appointment.id}`;

                        return (
                          <div key={appointment.id} className="flex flex-col gap-2 rounded-lg border border-slate-200 px-3 py-2 md:flex-row md:items-center md:justify-between">
                            <div>
                              <div className="text-sm font-medium text-slate-900">{appointment.title || appointment.appointmentType.name}</div>
                              <div className="text-xs text-slate-500">
                                {formatTreatmentPlanDate(appointment.startTime)} · {appointment.provider.name}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${appointmentStatusBadge(appointment.status)}`}>{tr(appointment.status)}</span>
                              <button
                                className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-sm"
                                onClick={() => setAppointmentTreatmentPlanLink(appointment.id, null)}
                                disabled={busyAction === actionKey}
                              >
                                {busyAction === actionKey ? <Loader2 size={14} className="animate-spin" /> : null} {tr('Unlink')}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {!selectedPlanAppointments.length ? <div className="text-sm text-slate-500">{tr('No visits linked to this treatment plan yet.')}</div> : null}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-3 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h5 className="text-sm font-semibold text-slate-900">{tr('Add Recent Visits')}</h5>
                        <p className="text-xs text-slate-500">{tr('Recent unlinked visits can be attached here without affecting scheduling or reminders.')}</p>
                      </div>
                      <div className="text-xs text-slate-500">{tr('Showing up to 12 visits')}</div>
                    </div>
                    <div className="space-y-2">
                      {recentEligibleAppointments.map((appointment) => {
                        const actionKey = `appointment:${appointment.id}`;

                        return (
                          <div key={appointment.id} className="flex flex-col gap-2 rounded-lg border border-slate-200 px-3 py-2 md:flex-row md:items-center md:justify-between">
                            <div>
                              <div className="text-sm font-medium text-slate-900">{appointment.title || appointment.appointmentType.name}</div>
                              <div className="text-xs text-slate-500">
                                {formatTreatmentPlanDate(appointment.startTime)} · {appointment.provider.name}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${appointmentStatusBadge(appointment.status)}`}>{tr(appointment.status)}</span>
                              <button
                                className="rounded-lg bg-ink px-3 py-1.5 text-sm text-white disabled:opacity-60"
                                onClick={() => setAppointmentTreatmentPlanLink(appointment.id, selectedPlan.id)}
                                disabled={busyAction === actionKey || selectedPlan.status === 'COMPLETED' || selectedPlan.status === 'DISCONTINUED'}
                              >
                                {busyAction === actionKey ? <Loader2 size={14} className="animate-spin" /> : null} {tr('Link Visit')}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {!recentEligibleAppointments.length ? <div className="text-sm text-slate-500">{tr('No recent eligible visits available to add.')}</div> : null}
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 p-3 space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div>
                        <h5 className="text-sm font-semibold text-slate-900">{tr('Linked Notes')}</h5>
                        <p className="text-xs text-slate-500">{tr('Recent chart notes attached to this treatment plan appear here.')}</p>
                      </div>
                      <div className="text-xs text-slate-500">{selectedPlan.linkedNotes.length} {tr('shown')}</div>
                    </div>
                    <div className="space-y-2">
                      {selectedPlan.linkedNotes.map((note) => (
                        <div key={note.id} className="rounded-lg border border-slate-200 px-3 py-2">
                          <div className="text-sm font-medium text-slate-900">{note.title}</div>
                          <div className="text-xs text-slate-500">
                            {formatTreatmentPlanDate(note.updatedAt)}
                            {note.authorName ? ` · ${note.authorName}` : ''}
                          </div>
                        </div>
                      ))}
                      {!selectedPlan.linkedNotes.length ? <div className="text-sm text-slate-500">{tr('No notes linked to this treatment plan yet.')}</div> : null}
                    </div>
                  </div>
                </div>
              </details>

              <details open className="rounded-xl border border-slate-200 bg-white overflow-hidden group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-900">
                  <div>
                    <div>{tr('Plan Overview')}</div>
                    <div className="mt-1 text-xs font-normal text-slate-500">{tr('The key plan settings stay easy to review and quick to update here.')}</div>
                  </div>
                  <span className="text-xs text-slate-500 group-open:hidden">{tr('Expand')}</span>
                  <span className="text-xs text-slate-500 hidden group-open:inline">{tr('Collapse')}</span>
                </summary>
                <div className="space-y-4 border-t border-slate-100 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input label={tr('Title')} value={draft.title} onChange={(value) => setDraft((prev) => (prev ? { ...prev, title: value } : prev))} />
                    <Input label={tr('Category')} value={draft.category} onChange={(value) => setDraft((prev) => (prev ? { ...prev, category: value } : prev))} />
                    <Select label={tr('Priority')} value={draft.priority} options={priorities} onChange={(value) => setDraft((prev) => (prev ? { ...prev, priority: value as TreatmentPlanPriority } : prev))} />
                    <Select label={tr('Status')} value={draft.status} options={statuses} onChange={(value) => setDraft((prev) => (prev ? { ...prev, status: value as TreatmentPlanStatus } : prev))} />
                    <Input label={tr('Suggested Visits')} type="number" value={draft.suggestedVisitCount} onChange={(value) => setDraft((prev) => (prev ? { ...prev, suggestedVisitCount: value } : prev))} />
                    <Input label={tr('Review Cadence (days)')} type="number" value={draft.reviewCadenceDays} onChange={(value) => setDraft((prev) => (prev ? { ...prev, reviewCadenceDays: value } : prev))} />
                    <Input label={tr('Progress %')} type="number" value={draft.progressPercent} onChange={(value) => setDraft((prev) => (prev ? { ...prev, progressPercent: value } : prev))} />
                    <Input label={tr('Adherence %')} type="number" value={draft.adherencePercent} onChange={(value) => setDraft((prev) => (prev ? { ...prev, adherencePercent: value } : prev))} />
                  </div>

                  <div className="rounded-xl border border-slate-200 p-3 space-y-2">
                    <div>
                      <h5 className="text-sm font-semibold text-slate-900">{tr('Diagnosis Codes')}</h5>
                      <p className="text-xs text-slate-500">{tr('Add up to three diagnosis codes. The first code is treated as the primary diagnosis.')}</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                      {draft.diagnosisCodes.map((value, index) => (
                        <Input
                          key={`diagnosis-code-${index}`}
                          label={tr(`Diagnosis Code ${index + 1}`)}
                          value={value}
                          onChange={(nextValue) =>
                            setDraft((prev) => {
                              if (!prev) return prev;
                              const nextCodes = [...prev.diagnosisCodes];
                              nextCodes[index] = nextValue;
                              return { ...prev, diagnosisCodes: nextCodes };
                            })
                          }
                        />
                      ))}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <Input label={tr('Start Date')} type="datetime-local" value={draft.startDate} onChange={(value) => setDraft((prev) => (prev ? { ...prev, startDate: value } : prev))} />
                    <Input label={tr('Target End Date')} type="datetime-local" value={draft.targetEndDate} onChange={(value) => setDraft((prev) => (prev ? { ...prev, targetEndDate: value } : prev))} />
                    <Input label={tr('Next Review')} type="datetime-local" value={draft.nextReviewAt} onChange={(value) => setDraft((prev) => (prev ? { ...prev, nextReviewAt: value } : prev))} />
                  </div>

                  <TextArea label={tr('Patient Instructions')} value={draft.patientInstructions} onChange={(value) => setDraft((prev) => (prev ? { ...prev, patientInstructions: value } : prev))} />
                  <TextArea label={tr('Clinician Notes')} value={draft.clinicianNotes} onChange={(value) => setDraft((prev) => (prev ? { ...prev, clinicianNotes: value } : prev))} />
                  <Input label={tr('Barriers (comma separated)')} value={draft.barriersText} onChange={(value) => setDraft((prev) => (prev ? { ...prev, barriersText: value } : prev))} />
                  <Input label={tr('Outcomes (comma separated)')} value={draft.outcomesText} onChange={(value) => setDraft((prev) => (prev ? { ...prev, outcomesText: value } : prev))} />
                </div>
              </details>

              <details className="rounded-xl border border-slate-200 bg-white overflow-hidden group">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-slate-900">
                  <div>
                    <div>{tr('Advanced Plan Details')}</div>
                    <div className="mt-1 text-xs font-normal text-slate-500">{tr('Goals, interventions, medications, monitoring, and care-team structure live here when you need more depth.')}</div>
                  </div>
                  <span className="text-xs text-slate-500 group-open:hidden">{tr('Expand')}</span>
                  <span className="text-xs text-slate-500 hidden group-open:inline">{tr('Collapse')}</span>
                </summary>
                <div className="space-y-4 border-t border-slate-100 p-4">
                  <Section title={tr('Goals')} onAdd={() => setDraft((prev) => (prev ? { ...prev, goals: [...prev.goals, { title: '', metric: '', targetValue: '' }] } : prev))}>
                {draft.goals.map((item, index) => (
                  <Row key={`goal-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, goals: prev.goals.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Title')} value={item.title || ''} onChange={(value) => updateGoal(index, { title: value })} />
                    <Input label={tr('Metric')} value={item.metric || ''} onChange={(value) => updateGoal(index, { metric: value })} />
                    <Input label={tr('Target')} value={item.targetValue || ''} onChange={(value) => updateGoal(index, { targetValue: value })} />
                    <Input label={tr('Unit')} value={item.unit || ''} onChange={(value) => updateGoal(index, { unit: value })} />
                    <Input label={tr('Timeframe (days)')} type="number" value={item.timeframeDays !== undefined ? String(item.timeframeDays) : ''} onChange={(value) => updateGoal(index, { timeframeDays: toInt(value) })} />
                    <TextArea label={tr('Description')} value={item.description || ''} onChange={(value) => updateGoal(index, { description: value })} />
                  </Row>
                ))}
                  </Section>

                  <Section
                    title={tr('Interventions')}
                    onAdd={() => setDraft((prev) => (prev ? { ...prev, interventions: [...prev.interventions, { summary: '', type: '', frequency: '' }] } : prev))}
                  >
                {draft.interventions.map((item, index) => (
                  <Row key={`intervention-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, interventions: prev.interventions.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Summary')} value={item.summary || ''} onChange={(value) => updateIntervention(index, { summary: value })} />
                    <Input label={tr('Type')} value={item.type || ''} onChange={(value) => updateIntervention(index, { type: value })} />
                    <Input label={tr('Frequency')} value={item.frequency || ''} onChange={(value) => updateIntervention(index, { frequency: value })} />
                    <Input label={tr('Duration (days)')} type="number" value={item.durationDays !== undefined ? String(item.durationDays) : ''} onChange={(value) => updateIntervention(index, { durationDays: toInt(value) })} />
                    <Input label={tr('Owner Role')} value={item.ownerRole || ''} onChange={(value) => updateIntervention(index, { ownerRole: value })} />
                    <TextArea label={tr('Instructions')} value={item.instructions || ''} onChange={(value) => updateIntervention(index, { instructions: value })} />
                  </Row>
                ))}
                  </Section>

                  <Section title={tr('Medications')} onAdd={() => setDraft((prev) => (prev ? { ...prev, medications: [...prev.medications, { medication: '', dose: '', frequency: '' }] } : prev))}>
                {draft.medications.map((item, index) => (
                  <Row key={`med-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, medications: prev.medications.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Medication')} value={item.medication || ''} onChange={(value) => updateMedication(index, { medication: value })} />
                    <Input label={tr('Dose')} value={item.dose || ''} onChange={(value) => updateMedication(index, { dose: value })} />
                    <Input label={tr('Route')} value={item.route || ''} onChange={(value) => updateMedication(index, { route: value })} />
                    <Input label={tr('Frequency')} value={item.frequency || ''} onChange={(value) => updateMedication(index, { frequency: value })} />
                    <Input label={tr('Duration (days)')} type="number" value={item.durationDays !== undefined ? String(item.durationDays) : ''} onChange={(value) => updateMedication(index, { durationDays: toInt(value) })} />
                    <Input label={tr('Indication')} value={item.indication || ''} onChange={(value) => updateMedication(index, { indication: value })} />
                    <TextArea label={tr('Instructions')} value={item.instructions || ''} onChange={(value) => updateMedication(index, { instructions: value })} />
                  </Row>
                ))}
                  </Section>

                  <Section title={tr('Monitoring')} onAdd={() => setDraft((prev) => (prev ? { ...prev, monitoring: [...prev.monitoring, { metric: '', targetRange: '' }] } : prev))}>
                {draft.monitoring.map((item, index) => (
                  <Row key={`monitor-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, monitoring: prev.monitoring.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Metric')} value={item.metric || ''} onChange={(value) => updateMonitoring(index, { metric: value })} />
                    <Input label={tr('Target Range')} value={item.targetRange || ''} onChange={(value) => updateMonitoring(index, { targetRange: value })} />
                    <Input label={tr('Frequency')} value={item.frequency || ''} onChange={(value) => updateMonitoring(index, { frequency: value })} />
                    <Input label={tr('Alert Threshold')} value={item.alertThreshold || ''} onChange={(value) => updateMonitoring(index, { alertThreshold: value })} />
                    <TextArea label={tr('Action If Out Of Range')} value={item.actionIfOutOfRange || ''} onChange={(value) => updateMonitoring(index, { actionIfOutOfRange: value })} />
                  </Row>
                ))}
                  </Section>

                  <Section title={tr('Follow-Up')} onAdd={() => setDraft((prev) => (prev ? { ...prev, followUps: [...prev.followUps, { modality: '', intervalDays: 30 }] } : prev))}>
                {draft.followUps.map((item, index) => (
                  <Row key={`follow-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, followUps: prev.followUps.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Modality')} value={item.modality || ''} onChange={(value) => updateFollowUp(index, { modality: value })} />
                    <Input label={tr('Interval (days)')} type="number" value={item.intervalDays !== undefined ? String(item.intervalDays) : ''} onChange={(value) => updateFollowUp(index, { intervalDays: toInt(value) })} />
                    <Input label={tr('Owner Role')} value={item.ownerRole || ''} onChange={(value) => updateFollowUp(index, { ownerRole: value })} />
                    <TextArea label={tr('Purpose')} value={item.purpose || ''} onChange={(value) => updateFollowUp(index, { purpose: value })} />
                  </Row>
                ))}
                  </Section>

                  <Section title={tr('Education')} onAdd={() => setDraft((prev) => (prev ? { ...prev, education: [...prev.education, { topic: '', objective: '' }] } : prev))}>
                {draft.education.map((item, index) => (
                  <Row key={`education-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, education: prev.education.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Topic')} value={item.topic || ''} onChange={(value) => updateEducation(index, { topic: value })} />
                    <Input label={tr('Objective')} value={item.objective || ''} onChange={(value) => updateEducation(index, { objective: value })} />
                    <Input label={tr('Materials')} value={item.materials || ''} onChange={(value) => updateEducation(index, { materials: value })} />
                    <label className="space-y-1 block">
                      <span className="text-xs uppercase tracking-wide text-slate-500">{tr('Required')}</span>
                      <label className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm">
                        <input type="checkbox" checked={Boolean(item.required)} onChange={(event) => updateEducation(index, { required: event.target.checked })} /> {tr('Required')}
                      </label>
                    </label>
                  </Row>
                ))}
                  </Section>

                  <Section title={tr('Escalation Rules')} onAdd={() => setDraft((prev) => (prev ? { ...prev, escalationRules: [...prev.escalationRules, { trigger: '', action: '', severity: 'medium' }] } : prev))}>
                {draft.escalationRules.map((item, index) => (
                  <Row key={`escalation-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, escalationRules: prev.escalationRules.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Trigger')} value={item.trigger || ''} onChange={(value) => updateEscalation(index, { trigger: value })} />
                    <Input label={tr('Action')} value={item.action || ''} onChange={(value) => updateEscalation(index, { action: value })} />
                    <Select label={tr('Severity')} value={item.severity || 'medium'} options={['low', 'medium', 'high']} onChange={(value) => updateEscalation(index, { severity: value as 'low' | 'medium' | 'high' })} />
                  </Row>
                ))}
                  </Section>

                  <Section title={tr('Care Team')} onAdd={() => setDraft((prev) => (prev ? { ...prev, careTeam: [...prev.careTeam, { role: '', memberName: '' }] } : prev))}>
                {draft.careTeam.map((item, index) => (
                  <Row key={`care-${index}`} onRemove={() => setDraft((prev) => (prev ? { ...prev, careTeam: prev.careTeam.filter((_, idx) => idx !== index) } : prev))}>
                    <Input label={tr('Role')} value={item.role || ''} onChange={(value) => updateCareTeam(index, { role: value })} />
                    <Input label={tr('Member Name')} value={item.memberName || ''} onChange={(value) => updateCareTeam(index, { memberName: value })} />
                    <Input label={tr('Responsibility')} value={item.responsibility || ''} onChange={(value) => updateCareTeam(index, { responsibility: value })} />
                    <Input label={tr('Contact')} value={item.contact || ''} onChange={(value) => updateCareTeam(index, { contact: value })} />
                  </Row>
                ))}
                  </Section>
                </div>
              </details>
            </>
          )}
        </div>
      </div>

      {createDialogOpen && (
        <div className="fixed inset-0 z-40 flex items-start justify-center bg-black/40 p-4" onClick={() => setCreateDialogOpen(false)}>
          <div className="mt-8 w-full max-w-2xl rounded-2xl border border-slate-200 bg-white p-4 shadow-xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-3 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-500">{tr('Treatment Plans')}</p>
                <h4 className="text-lg font-semibold text-slate-900">{tr('Assign Treatment Plan')}</h4>
              </div>
              <button className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-700" onClick={() => setCreateDialogOpen(false)}>
                {tr('Close')}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={createTemplateCode} onChange={(event) => setCreateTemplateCode(event.target.value)}>
                <option value="">{tr('Select template...')}</option>
                {templates.map((template) => (
                  <option key={template.code} value={template.code}>
                    {template.name}
                    {template.active ? '' : ` (${tr('inactive')})`}
                  </option>
                ))}
              </select>
              <input className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder={tr('Plan Name')} value={createTitle} onChange={(event) => setCreateTitle(event.target.value)} />
              <select className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={createPriority} onChange={(event) => setCreatePriority(event.target.value as TreatmentPlanPriority)}>
                {priorities.map((priority) => (
                  <option key={priority} value={priority}>
                    {tr(priority)}
                  </option>
                ))}
              </select>
              {createDiagnosisCodes.map((value, index) => (
                <input
                  key={`create-diagnosis-${index}`}
                  className="border border-slate-300 rounded-lg px-3 py-2 text-sm"
                  placeholder={tr(`Diagnosis Code ${index + 1}`)}
                  value={value}
                  onChange={(event) =>
                    setCreateDiagnosisCodes((prev) => {
                      const next = [...prev];
                      next[index] = event.target.value;
                      return next;
                    })
                  }
                />
              ))}
              <input type="number" min={1} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" placeholder={tr('Suggested Visits')} value={createSuggestedVisitCount} onChange={(event) => setCreateSuggestedVisitCount(event.target.value)} />
              <input type="datetime-local" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={createStartDate} onChange={(event) => setCreateStartDate(event.target.value)} />
              <input type="datetime-local" className="border border-slate-300 rounded-lg px-3 py-2 text-sm" value={createTargetEndDate} onChange={(event) => setCreateTargetEndDate(event.target.value)} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm" onClick={() => setCreateDialogOpen(false)}>
                {tr('Cancel')}
              </button>
              <button onClick={createPlan} disabled={saving || !createTitle.trim()} className="inline-flex items-center justify-center gap-2 rounded-lg bg-ink px-3 py-2 text-sm text-white disabled:opacity-60">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} {tr('Assign Plan')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, onAdd, children }: { title: string; onAdd: () => void; children: ReactNode }) {
  const { tr } = useI18n();
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-semibold text-slate-800">{title}</h5>
        <button className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700" onClick={onAdd}>
          <Plus size={12} /> {tr('Add')}
        </button>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ children, onRemove }: { children: ReactNode; onRemove: () => void }) {
  const { tr } = useI18n();
  return (
    <div className="rounded-lg border border-slate-200 p-2 space-y-2">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">{children}</div>
      <button className="text-xs text-rose-700" onClick={onRemove}>
        {tr('Remove row')}
      </button>
    </div>
  );
}

function Input({ label, value, onChange, type = 'text' }: { label: string; value: string; onChange: (value: string) => void; type?: string }) {
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <input className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm" type={type} value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function Select({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  const { tr } = useI18n();
  return (
    <label className="space-y-1 block">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <SelectMenu
        value={value}
        onChange={onChange}
        buttonAriaLabel={label}
        triggerClassName="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm"
        options={options.map((option) => ({ value: option, label: tr(option) }))}
      />
    </label>
  );
}

function TextArea({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="space-y-1 block md:col-span-2">
      <span className="text-xs uppercase tracking-wide text-slate-500">{label}</span>
      <textarea className="w-full min-h-[88px] rounded-lg border border-slate-200 px-3 py-2 text-sm" value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}
